/**
 * Ist die App auf dem neuesten Stand?
 *
 * Jeder Build schreibt seine Kennung in die App (__BUILD_ID__) und in
 * version.json auf dem Server. Stimmen beide ueberein, laeuft der neueste
 * Stand. version.json wird dabei am Service Worker vorbei frisch geholt.
 */
import { rootFile } from '../platform/endpoints.ts';

export type UpdateStatus =
  | { state: 'current'; local: string }
  | { state: 'outdated'; local: string; remote: string }
  | { state: 'unknown'; local: string; reason: string };

/** Zeitstempel einer Build-Kennung als lesbares Datum. */
export function buildDate(id: string): string {
  const n = Number(id);
  if (!Number.isFinite(n) || n <= 0) return 'unbekannt';
  return new Date(n).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export async function checkForUpdate(): Promise<UpdateStatus> {
  const local = __BUILD_ID__;
  if (!import.meta.env.PROD) return { state: 'unknown', local, reason: 'Entwicklungsmodus' };
  if (!navigator.onLine) return { state: 'unknown', local, reason: 'Kein Netz' };
  try {
    const res = await fetch(`${rootFile('version.json')}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return { state: 'unknown', local, reason: `Server antwortet ${res.status}` };
    if (!(res.headers.get('content-type') ?? '').includes('json')) return { state: 'unknown', local, reason: 'Versionsdatei nicht gefunden' };
    const data = (await res.json()) as { build?: string };
    const remote = String(data.build ?? '');
    if (!remote) return { state: 'unknown', local, reason: 'Keine Versionsangabe' };
    return remote === local ? { state: 'current', local } : { state: 'outdated', local, remote };
  } catch {
    return { state: 'unknown', local, reason: 'Server nicht erreichbar' };
  }
}

/**
 * Holt den neuesten Stand – sofort:
 *   1. Offline-Zwischenspeicher leeren und Service Worker abmelden,
 *   2. die Startseiten am Browser-Zwischenspeicher vorbei neu holen,
 *   3. mit einer frischen Adresse neu laden (?u=…), die der Start gleich
 *      wieder entfernt (pwa/register.ts).
 * Daten der App (IndexedDB, Cloud) bleiben unberuehrt.
 */
export async function applyUpdate(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      for (const reg of await navigator.serviceWorker.getRegistrations()) await reg.unregister();
    }
    if ('caches' in window) {
      for (const key of await caches.keys()) await caches.delete(key);
    }
    // Startseiten frisch in den HTTP-Zwischenspeicher, damit das Neuladen
    // keine alte Fassung aus dem Browser nimmt. Hoechstens 4 s warten.
    const pages = [location.href.split('#')[0] ?? '', rootFile(''), rootFile('cloud/'), rootFile('system/')];
    await Promise.race([
      Promise.all(pages.map((u) => fetch(u, { cache: 'reload', credentials: 'same-origin' }).catch(() => undefined))),
      new Promise((r) => setTimeout(r, 4000)),
    ]);
  } finally {
    const url = new URL(location.href);
    url.searchParams.set('u', Date.now().toString(36));
    location.replace(url.href);
  }
}
